import React, { useEffect, useState } from "react";
import { Loader2, Plus, ShieldCheck, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";

/**
 * Insurer register — captures per-insurer defaults so new insurance jobs
 * pre-fill the correct excess percentage / amount instead of leaving the
 * field blank.
 */
export const AdminInsurers = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await adminApi.listInsurers(q ? { query: q } : {}));
    } catch {
      toast.error("Could not load insurers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (r) => {
    if (!window.confirm(`Delete insurer "${r.name}"? Jobs already using it stay untouched.`)) return;
    try {
      await adminApi.deleteInsurer(r.id);
      toast.success("Insurer deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-insurers-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">
            Insurers
          </h1>
          <p className="text-[#3f4b52] mt-1">
            Add each insurer once with their default excess percentage — the Job form auto-fills the excess when the insurer is chosen.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setEditing(null); setShowForm(true); }}
          data-testid="insurer-new-btn"
          className="bg-[#f28a16] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:brightness-110"
        >
          <Plus size={14} /> New insurer
        </button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-6 flex items-center gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name / email / contact…"
          className="w-full max-w-md h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
          data-testid="insurer-search"
        />
        <button
          type="submit"
          className="border border-[#cfd7db] px-4 h-10 rounded-md text-xs font-display uppercase tracking-widest hover:bg-[#f5f6f6]"
        >
          Search
        </button>
      </form>

      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-[#3f4b52]">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading insurers…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#3f4b52]">
            <ShieldCheck size={20} className="mx-auto text-[#f28a16] mb-2" />
            No insurers yet. Add one to start auto-filling excess percentages on new insurance jobs.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#f5f6f6] font-display uppercase tracking-widest text-xs text-[#3f4b52]">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-right px-4 py-3">Excess %</th>
                <th className="text-right px-4 py-3">Excess amount</th>
                <th className="text-left px-4 py-3">Claims email</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[#eef2f4]" data-testid={`insurer-row-${r.id}`}>
                  <td className="px-4 py-3 font-display font-bold text-[#111619]">{r.name}</td>
                  <td className="px-4 py-3 text-right font-mono">{Number(r.default_excess_pct || 0).toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {Number(r.default_excess_amount || 0) > 0 ? `R ${Number(r.default_excess_amount).toFixed(2)}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-[#3f4b52]">{r.claims_email || "—"}</td>
                  <td className="px-4 py-3 text-[#3f4b52]">{r.phone || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-display uppercase tracking-wider ${r.active ? "bg-[#10b981] text-white" : "bg-[#9aa6ad] text-white"}`}>
                      {r.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => { setEditing(r); setShowForm(true); }}
                      data-testid={`insurer-edit-${r.id}`}
                      className="text-[#f28a16] hover:underline text-xs inline-flex items-center gap-1"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={() => remove(r)}
                      data-testid={`insurer-del-${r.id}`}
                      className="ml-3 text-[#dc2626] hover:underline text-xs inline-flex items-center gap-1"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <InsurerForm
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
};

const InsurerForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: "",
    default_excess_pct: 0,
    default_excess_amount: 0,
    claims_email: "",
    phone: "",
    contact_person: "",
    notes: "",
    active: true,
    ...(initial || {}),
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Insurer name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        default_excess_pct: Number(form.default_excess_pct) || 0,
        default_excess_amount: Number(form.default_excess_amount) || 0,
        claims_email: form.claims_email || null,
      };
      if (initial?.id) {
        await adminApi.updateInsurer(initial.id, payload);
        toast.success("Insurer updated");
      } else {
        await adminApi.createInsurer(payload);
        toast.success("Insurer added");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="insurer-form-modal">
      <form onSubmit={save} className="bg-white rounded-md shadow-xl max-w-lg w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">
              {initial ? "Edit insurer" : "New insurer"}
            </div>
            <h2 className="font-display font-black uppercase text-xl text-[#111619] mt-1">
              {initial ? initial.name : "Add insurer"}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="text-[#3f4b52]" aria-label="Close" data-testid="insurer-form-close">
            <X size={18} />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name" span={2}>
            <input value={form.name} onChange={set("name")} className="w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm" data-testid="insurer-name" required />
          </Field>
          <Field label="Default excess %">
            <input type="number" step="0.01" value={form.default_excess_pct} onChange={set("default_excess_pct")} className="w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm" data-testid="insurer-excess-pct" />
          </Field>
          <Field label="Default excess amount (R)">
            <input type="number" step="0.01" value={form.default_excess_amount} onChange={set("default_excess_amount")} className="w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm" data-testid="insurer-excess-amount" />
          </Field>
          <Field label="Claims email" span={2}>
            <input type="email" value={form.claims_email || ""} onChange={set("claims_email")} className="w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm" placeholder="claims@insurer.co.za" data-testid="insurer-claims-email" />
          </Field>
          <Field label="Contact person">
            <input value={form.contact_person || ""} onChange={set("contact_person")} className="w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm" />
          </Field>
          <Field label="Phone">
            <input value={form.phone || ""} onChange={set("phone")} className="w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm" />
          </Field>
          <Field label="Notes" span={2}>
            <textarea value={form.notes || ""} onChange={set("notes")} rows={2} className="w-full px-3 py-2 border border-[#cfd7db] rounded-md text-sm" />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active")(e.target.checked)} className="accent-[#f28a16]" />
            Active
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md hover:bg-[#f5f6f6]">
            Cancel
          </button>
          <button type="submit" disabled={saving} data-testid="insurer-save-btn"
                  className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-10 rounded-md hover:brightness-110 disabled:opacity-60 inline-flex items-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />}
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, span = 1, children }) => (
  <div className={span === 2 ? "sm:col-span-2" : ""}>
    <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default AdminInsurers;
